import { Hidden, Container, Typography, Box, Theme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { SxClassProps } from '@/types/common';
import { Member } from '@/types/providerSegmentation';
import { Language } from '@/__generated__/globalTypes';
import { getProviderDetails_getSEOSegmentationCaregiverDetails_caregiver_profiles_commonCaregiverProfile_bio as ProviderDetailsBioType } from '@/__generated__/getProviderDetails';
import ReadMore from './ReadMore';

const classes: SxClassProps = {
  bullets: {
    textTransform: 'capitalize',
    display: 'list-item',
    float: 'left',
    marginLeft: '30px',
    '&::marker': {
      color: 'care.blue.300',
    },
    '&:first-of-type': {
      display: 'inline-block',
      marginLeft: '0px',
    },
  },
};

const mappedLanguages: { [key in Language]: string } = {
  AFRIKAANS: 'Afrikaans',
  ALBANIAN: 'Albanian',
  ARABIC: 'Arabic',
  ARMENIAN: 'Armenian',
  AZERBAIJANI: 'Azerbaijani',
  BELARUSIAN: 'Belarusian',
  BENGALI: 'Bengali',
  BOSNIAN: 'Bosnian',
  BULGARIAN: 'Bulgarian',
  CAMBODIAN: 'Cambodian',
  CATALAN: 'Catalan',
  CHINESE: 'Chinese',
  CROATIAN: 'Croatian',
  CZECH: 'Czech',
  DANISH: 'Danish',
  DUTCH: 'Dutch',
  ENGLISH: 'English',
  ESTONIAN: 'Estonian',
  FINNISH: 'Finnish',
  FRENCH: 'French',
  GEORGIAN: 'Georgian',
  GERMAN: 'German',
  GREEK: 'Greek',
  HEBREW: 'Hebrew',
  HINDI: 'Hindi',
  HUNGARIAN: 'Hungarian',
  ICELANDIC: 'Icelandic',
  INDONESIAN: 'Indonesian',
  ITALIAN: 'Italian',
  JAPANESE: 'Japanese',
  KAZAKH: 'Kazakh',
  KOREAN: 'Korean',
  KURDISH: 'Kurdish',
  LAOTIAN: 'Laotian',
  LATVIAN: 'Latvian',
  LITHUANIAN: 'Lithuanian',
  MADARIN: 'Mandarin',
  MALAY: 'Malay',
  MANDARIN: 'Mandarin',
  MOLDAVIAN: 'Moldavian',
  MONGOLIAN: 'Mongolian',
  NEPALI: 'Nepali',
  NORWEGIAN: 'Norwegian',
  PERSIAN: 'Persian',
  POLISH: 'Polish',
  PORTUGUESE: 'Portuguese',
  PORTUGUESE_BRAZIL: 'Brazilian Portuguese',
  PUNJABI: 'Punjabi',
  ROMANIAN: 'Romanian',
  RUSSIAN: 'Russian',
  SERBIAN: 'Serbian',
  SERBO_CROATIAN: 'Serbo Croatian',
  SESOTHO: 'Sesotho',
  SIGN_ASL: 'Sign (ASL)',
  SIGN_LANGUAGE_ASL: 'Sign Language (ASL)',
  SIGN_LANGUAGE_SEE: 'Sign Language (SEE)',
  SIGN_SEE: 'Sign (SEE)',
  SINDHI: 'Sindhi',
  SLOVAK: 'Slovak',
  SLOVENIAN: 'Slovenian',
  SOMALI: 'Somali',
  SPANISH: 'Spanish',
  SUDANESE: 'Sudanese',
  SWAHILI: 'Swahili',
  SWEDISH: 'Swedish',
  TAGALOG: 'Tagalog',
  TAJIK: 'Tajik',
  TAMIL: 'Tamil',
  TELUGU: 'Telugu',
  THAI: 'Thai',
  TONGA: 'Tonga',
  TURKISH: 'Turkish',
  UKRAINIAN: 'Ukrainian',
  UKRANIAN: 'Ukranian',
  URDU: 'Urdu',
  UZBEK: 'Uzbek',
  VIETNAMESE: 'Vietnamese',
  YIDDISH: 'Yiddish',
  ZULU: 'Zulu',
};

const ProviderIntro = ({
  memberDetail,
  profileDesc,
}: {
  memberDetail: Member;
  profileDesc: ProviderDetailsBioType['experienceSummary'];
}) => {
  const isMobileOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  return (
    <Container maxWidth="md">
      <Box pt={{ xs: 1, md: 4 }} pb={3}>
        <Typography variant={isMobileOrSmaller ? 'h2' : 'h1'} mb={2} textAlign={{ md: 'left' }}>
          Meet {memberDetail.firstName}
        </Typography>
        <Box display="flex" flexWrap="wrap" mb={3} width="100%">
          {memberDetail.age !== null && (
            <Typography variant="h4" sx={classes.bullets}>
              {memberDetail.age}yrs
            </Typography>
          )}
          {memberDetail.gender && (
            <Typography variant="h4" sx={classes.bullets}>
              {memberDetail.gender.toLowerCase()}
            </Typography>
          )}
          <Typography variant="h4" sx={classes.bullets}>
            Speaks{' '}
            {memberDetail.languages.map((language: string, index: number) => {
              if (memberDetail.languages.length - 1 > index)
                return `${mappedLanguages[language as Language]}, `;
              return mappedLanguages[language as Language];
            })}
          </Typography>
        </Box>
        {profileDesc && (
          <Hidden implementation="css" smUp>
            <ReadMore
              gutterBottom
              component="div"
              variant="body2"
              charLimit={155}
              value={profileDesc}
              showMoreLabel="more"
            />
          </Hidden>
        )}
        {profileDesc && (
          <Hidden implementation="css" smDown>
            <Typography variant="body2">{profileDesc}</Typography>
          </Hidden>
        )}
      </Box>
    </Container>
  );
};

export default ProviderIntro;
